/**
 * Réglages — §A (profil) & §2 (intégrations). Gère les sports paramétrés
 * (niveau, orientation, fréquence, jours) et les connexions aux sources de
 * données (Apple Health, Health Connect, Strava, Garmin, module Santé Life).
 * Le module ne duplique pas les données santé brutes : il les consomme.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SyncRoundedIcon from '@mui/icons-material/SyncRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'

import { performanceApi } from '../api/performanceApi'
import {
  Loading,
  PERF_ACCENT,
  Pill,
  SectionTitle,
  cardSx,
  darkFieldSx,
} from './shared'

const LEVELS = {
  beginner: 'Débutant',
  intermediate: 'Intermédiaire',
  advanced: 'Avancé',
}

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

const PROVIDERS = [
  { value: 'apple_health', label: 'Apple Health' },
  { value: 'health_connect', label: 'Health Connect' },
  { value: 'strava', label: 'Strava' },
  { value: 'garmin', label: 'Garmin Health' },
  { value: 'life_health', label: 'Module Santé Life' },
]

// Fournisseurs qui se connectent par OAuth serveur (vs. push depuis l'app mobile).
const OAUTH_PROVIDERS = ['strava', 'garmin']

const STATUS_COLOR = {
  connected: '#3ddc97',
  disconnected: 'rgba(255,255,255,0.4)',
  error: '#ff4d4f',
  pending: '#fbbf24',
}

const STATUS_LABELS = {
  connected: 'Connectée',
  disconnected: 'Déconnectée',
  error: 'Erreur',
  pending: 'En attente',
}

export default function SettingsView() {
  const [prefs, setPrefs] = useState([])
  const [integrations, setIntegrations] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [provider, setProvider] = useState('apple_health')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')

  const load = async () => {
    setLoading(true)
    const [p, i] = await Promise.all([
      performanceApi.preferences.list(),
      performanceApi.integrations.list(),
    ])
    setPrefs(p)
    setIntegrations(i)
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const updatePref = async (id, body) => {
    await performanceApi.preferences.update(id, body)
    await load()
  }

  const toggleDay = (pref, dayIdx) => {
    const days = Array.isArray(pref.available_weekdays)
      ? pref.available_weekdays
      : []
    const next = days.includes(dayIdx)
      ? days.filter((d) => d !== dayIdx)
      : [...days, dayIdx].sort((a, b) => a - b)
    updatePref(pref.id, { available_weekdays: next })
  }

  const addIntegration = async () => {
    setBusy(true)
    try {
      await performanceApi.integrations.create({ provider, status: 'pending' })
      setAdding(false)
      await load()
      // Pour Strava/Garmin, enchaîne directement sur le consentement OAuth.
      if (OAUTH_PROVIDERS.includes(provider)) {
        await connectOAuth(provider)
      }
    } finally {
      setBusy(false)
    }
  }

  const sync = async (id) => {
    await performanceApi.syncIntegration(id)
    await load()
  }

  // Lance l'OAuth d'un fournisseur web : ouvre la fenêtre de consentement si le
  // serveur est configuré, sinon affiche un message honnête.
  const connectOAuth = async (prov) => {
    setNotice('')
    setBusy(true)
    try {
      const r = await performanceApi.oauthStart(prov)
      const data = (r && r.data) || {}
      if (r && r.status >= 200 && r.status < 300 && data.authorize_url) {
        window.open(data.authorize_url, '_blank', 'noopener,noreferrer')
        setNotice('Fenêtre de connexion ouverte. Revenez ici après avoir autorisé l’accès.')
      } else {
        setNotice(
          data.detail ||
            'Connexion OAuth indisponible : clés serveur non configurées pour ce fournisseur.',
        )
      }
    } finally {
      setBusy(false)
      await load()
    }
  }

  if (loading) return <Loading label="Chargement de vos réglages…" />

  const usedProviders = integrations.map((i) => i.provider)

  return (
    <Box>
      <SectionTitle>Mes sports</SectionTitle>
      {prefs.length === 0 ? (
        <Box sx={{ ...cardSx, mb: 4 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
            Aucun sport paramétré.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mb: 4 }}>
          {prefs.map((p) => (
            <Box key={p.id} sx={cardSx}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 700 }}>
                  {p.sport_name}
                </Typography>
                <Pill label={LEVELS[p.level] || p.level} />
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
                <TextField
                  select
                  size="small"
                  label="Niveau"
                  value={p.level}
                  onChange={(e) => updatePref(p.id, { level: e.target.value })}
                  sx={{ ...darkFieldSx, minWidth: 160 }}
                >
                  {Object.entries(LEVELS).map(([v, l]) => (
                    <MenuItem key={v} value={v}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="number"
                  size="small"
                  label="Séances/sem."
                  value={p.target_sessions_per_week}
                  onChange={(e) =>
                    updatePref(p.id, {
                      target_sessions_per_week: Number(e.target.value) || 1,
                    })
                  }
                  sx={{ ...darkFieldSx, width: 130 }}
                  inputProps={{ min: 1, max: 14 }}
                />
              </Stack>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 0.75 }}
              >
                Jours disponibles
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {WEEKDAYS.map((d, i) => {
                  const days = Array.isArray(p.available_weekdays)
                    ? p.available_weekdays
                    : []
                  return (
                    <Chip
                      key={d}
                      label={d}
                      size="small"
                      onClick={() => toggleDay(p, i)}
                      sx={{
                        bgcolor: days.includes(i)
                          ? 'rgba(255,106,61,0.25)'
                          : 'rgba(255,255,255,0.06)',
                        color: '#fff',
                      }}
                    />
                  )
                })}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <SectionTitle>Intégrations & données</SectionTitle>
        <Button
          onClick={() => setAdding(true)}
          sx={{ color: PERF_ACCENT, textTransform: 'none', fontWeight: 600 }}
        >
          + Connecter une source
        </Button>
      </Stack>
      {notice && (
        <Box sx={{ ...cardSx, mb: 1.5, borderColor: 'rgba(91,140,255,0.4)' }}>
          <Typography sx={{ color: '#9db6ff', fontSize: 13 }}>{notice}</Typography>
        </Box>
      )}
      {integrations.length === 0 ? (
        <Box sx={cardSx}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
            Aucune source connectée. Connectez Apple Health, Health Connect,
            Strava ou Garmin pour importer automatiquement vos activités et vos
            signaux de récupération.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {integrations.map((i) => (
            <Box key={i.id} sx={cardSx}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                    {PROVIDERS.find((p) => p.value === i.provider)?.label ||
                      i.provider}
                  </Typography>
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                  >
                    {i.last_sync_at
                      ? `Dernière synchro : ${new Date(
                          i.last_sync_at,
                        ).toLocaleString('fr-FR')}`
                      : 'Jamais synchronisée'}
                    {i.error_message ? ` · ${i.error_message}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Pill
                    label={STATUS_LABELS[i.status] || i.status}
                    color={STATUS_COLOR[i.status] || PERF_ACCENT}
                  />
                  {OAUTH_PROVIDERS.includes(i.provider) && i.status !== 'connected' && (
                    <Button
                      size="small"
                      startIcon={<LinkRoundedIcon />}
                      onClick={() => connectOAuth(i.provider)}
                      disabled={busy}
                      sx={{ color: '#5b8cff', textTransform: 'none' }}
                    >
                      Connecter
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<SyncRoundedIcon />}
                    onClick={() => sync(i.id)}
                    sx={{ color: PERF_ACCENT, textTransform: 'none' }}
                  >
                    Synchroniser
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth>
        <DialogTitle>Connecter une source</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Fournisseur"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          >
            {PROVIDERS.filter(
              (p) => !usedProviders.includes(p.value),
            ).map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography
            sx={{ color: 'rgba(0,0,0,0.55)', fontSize: 13, mt: 1.5 }}
          >
            {OAUTH_PROVIDERS.includes(provider)
              ? 'Strava et Garmin se connectent via OAuth : une fenêtre de consentement s’ouvrira (si les clés serveur sont configurées).'
              : 'Apple Health et Health Connect ne s’interrogent pas depuis le serveur : c’est l’application mobile qui pousse vos données. La connexion reste « en attente » jusqu’à la première synchronisation envoyée par l’app.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Annuler</Button>
          <Button
            onClick={addIntegration}
            disabled={busy}
            variant="contained"
            sx={{ bgcolor: PERF_ACCENT, color: '#0b0f1f' }}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
